import { motion, useScroll, useTransform } from 'framer-motion';
import { FaUsers, FaBriefcase, FaHandshake } from 'react-icons/fa';
import React, { useState, useEffect } from 'react';
import SocialLinks from '../components/common/SocialLinks';
import { setDefaultSEO } from '../utils/seoUtils';
import './About.css';
import { useNavigate } from 'react-router-dom';

const aboutHeroImage = "https://images.unsplash.com/photo-1521737711867-e3b97375f902?q=80&w=1920&auto=format&fit=crop";

function About() {
  const navigate = useNavigate();
  
  // Set default SEO for about page
  useEffect(() => {
    setDefaultSEO('About');
  }, []);

  // Scroll-based animations
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [0, 1]);
  const yPos = useTransform(scrollYProgress, [0, 0.2], [100, 0]);

  const features = [
    {
      icon: <FaUsers />,
      title: "Job Seekers First",
      description: "We prioritize connecting talented individuals with their dream careers through our comprehensive job listing platform."
    },
    {
      icon: <FaBriefcase />,
      title: "Information Platform",
      description: "As an informational job-sharing platform, we provide valuable insights and opportunities across various industries."
    },
    {
      icon: <FaHandshake />,
      title: "Career Growth",
      description: "While we don't directly handle recruitment, we facilitate connections between job seekers and potential employers."
    }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  const statCounterVariants = {
    hidden: { scale: 0 },
    visible: {
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        duration: 0.8
      }
    }
  };

  return (
    <div className="about-page">
      {/* SEO Meta Tags - Set via setDefaultSEO in useEffect */}
      
      {/* Hero Section with Parallax and Image */}
      <motion.div 
        className="about-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="about-hero-overlay"></div>
        <div className="about-hero-image">
          <img src={aboutHeroImage} alt="JobsOpportunity Team" />
        </div>
        <motion.div 
          className="container about-hero-content"
          initial={{ y: 50 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <motion.h1
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            About JobsOpportunity.in
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            Your Gateway to Career Success
          </motion.p>
        </motion.div>
      </motion.div>

      {/* Mission Section with Scroll Animation */}
      <section className="mission-section">
        <motion.div 
          className="container"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="mission-content">
            <motion.h2
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl font-bold mb-6"
            >
              Our Mission & Vision
            </motion.h2>
            <motion.p
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg leading-relaxed"
            >
              Jobs opportunity is a informational website that provides the
              latest job openings and career updates. The company does
              not recruit candidates directly but shares job opportunities
              from various sources to help job seekers find relevant
              employment options across different industries. Our mission
              is to bridge the gap between job seekers and employers,
              empowering individuals to achieve their professional goals.
            </motion.p>
          </div>
        </motion.div>
      </section>

      {/* Features Section with Stagger Animation */}
      <section className="features-section">
        <div className="container">
          <motion.div 
            className="features-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {features.map((feature, index) => (
              <motion.div 
                key={feature.title}
                className="feature-card"
                variants={itemVariants}
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 8px 16px rgba(0,0,0,0.1)"
                }}
              >
                <motion.div 
                  className="feature-icon"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  {feature.icon}
                </motion.div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Stats Section with Counter Animation */}
      <section className="stats-section">
        <div className="container">
          <motion.div 
            className="stats-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div 
              className="stat-card"
              variants={statCounterVariants}
              whileHover={{ 
                scale: 1.1,
                backgroundColor: "rgba(255,255,255,0.2)" 
              }}
            >
              <motion.h3
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 1 }}
              >
                <Counter from={0} to={10} duration={2} />K+
              </motion.h3>
              <p>Active Jobs</p>
            </motion.div>
            <motion.div 
              className="stat-card"
              variants={statCounterVariants}
              whileHover={{ 
                scale: 1.1,
                backgroundColor: "rgba(255,255,255,0.2)" 
              }}
            >
              <motion.h3
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.2 }}
              >
                <Counter from={0} to={5} duration={2} />K+
              </motion.h3>
              <p>Companies</p>
            </motion.div>
            <motion.div 
              className="stat-card"
              variants={statCounterVariants}
              whileHover={{ 
                scale: 1.1,
                backgroundColor: "rgba(255,255,255,0.2)" 
              }}
            >
              <motion.h3
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.4 }}
              >
                <Counter from={0} to={50} duration={2} />K+
              </motion.h3>
              <p>Job Seekers</p>
              
            </motion.div>
            
          </motion.div>
          <SocialLinks className="about-social-links" />
          
        </div>
      </section>

      {/* Add Explore Blogs Section before footer */}
      <section className="explore-blogs-section">
        <div className="container">
          <motion.div 
            className="explore-blogs-content"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2>Stay Updated with Our Latest Insights</h2>
            <p>Discover career tips, industry trends, and professional advice through our regularly updated blog posts.</p>
            <motion.button
              className="explore-button"
              onClick={() => navigate('/blogs')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Explore Blogs
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

// Counter component for animated statistics
function Counter({ from, to, duration }) {
  const [count, setCount] = useState(from);

  useEffect(() => {
    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = (timestamp - startTime) / (duration * 1000);

      if (progress < 1) {
        setCount(Math.floor(from + (to - from) * progress));
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(to);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [from, to, duration]);

  return count;
}

export default About; 